import { useWeeklyChartData } from "@/components/operators/useWeeklyChartData";

const BAR_MAX_HEIGHT_PX = 28;

/**
 * Mini bar chart showing pickup+delivery events per day for the next 7 days.
 * Bars are clickable: triggers onDayClick(isoDate) for drill-down filtering.
 */
export function OperatorWeeklyChart({ operator, onDayClick, selectedDay }) {
  const data = useWeeklyChartData(operator);
  if (data.total === 0) return null;

  return (
    <div className="hidden md:flex items-end gap-1.5 shrink-0" data-testid={`operator-weekly-chart-${operator.id}`}>
      <div className="flex flex-col items-end justify-end mr-1">
        <span className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold leading-none">7gg</span>
        <span className="text-xs font-mono font-semibold text-zinc-900">{data.total}</span>
      </div>
      {data.days.map((d, i) => (
        <ChartBar
          key={d.iso}
          day={d} index={i} max={data.max}
          isSelected={selectedDay === d.iso}
          onDayClick={onDayClick}
          operatorId={operator.id}
        />
      ))}
    </div>
  );
}

function ChartBar({ day, index, max, isSelected, onDayClick, operatorId }) {
  const sum = day.pickups + day.deliveries;
  const pickupH = (day.pickups / max) * BAR_MAX_HEIGHT_PX;
  const deliveryH = (day.deliveries / max) * BAR_MAX_HEIGHT_PX;
  const isToday = index === 0;
  const interactive = sum > 0 && onDayClick;
  const tooltip = `${day.dow} ${day.dayNum}: ${day.pickups} ritiri, ${day.deliveries} consegne — clicca per filtrare`;

  const handleClick = (e) => {
    e.stopPropagation();
    if (interactive) onDayClick(day.iso);
  };

  let labelClass = "text-zinc-400";
  if (isSelected) labelClass = "text-[#002FA7] font-bold";
  else if (isToday) labelClass = "text-[#002FA7] font-semibold";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!interactive}
      data-testid={`operator-chart-day-${operatorId}-${day.iso}`}
      title={tooltip}
      className={`flex flex-col items-center gap-0.5 ${interactive ? "cursor-pointer hover:opacity-80 transition" : "cursor-default"}`}
    >
      <div className={`h-[30px] w-[10px] flex flex-col-reverse rounded-sm overflow-hidden bg-zinc-100 ${isSelected ? "ring-2 ring-[#002FA7] ring-offset-1" : ""}`}>
        {day.pickups > 0 && <div style={{ height: `${pickupH}px` }} className="w-full bg-[#002FA7]" />}
        {day.deliveries > 0 && <div style={{ height: `${deliveryH}px` }} className="w-full bg-[#059669]" />}
      </div>
      <span className={`text-[9px] leading-none ${labelClass}`}>{day.dow[0]}</span>
      {sum > 0 && <span className="text-[9px] font-mono leading-none text-zinc-600">{sum}</span>}
    </button>
  );
}

export function WeeklyChartLegend() {
  return (
    <div className="hidden md:flex items-center gap-3 text-[10px] text-zinc-500" data-testid="operators-chart-legend">
      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#002FA7]" />Ritiri</span>
      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#059669]" />Consegne</span>
      <span className="text-zinc-400">·</span>
      <span>prossimi 7 giorni</span>
    </div>
  );
}
